import authService from './authService';
import {
  ProductionActivity,
  ProductionActivityFormData,
  ProductionActivitySearchModel,
  ProductionEmployee,
  ProductionEmployeeFormData,
  ProductionEmployeeSearchModel,
  BillOfMaterial,
  BillOfMaterialCreateData,
  BillOfMaterialUpdateData,
  BillOfMaterialSearchModel,
  PendingManufacturingProduct,
  ProductionActivityLog,
  ProductionActivityLogCreateData,
  ProductionActivityLogUpdateData,
  ProductionActivityLogSearchModel,
  ProductionOrder,
  ProductionOrderCreateData,
  ProductionOrderUpdateData,
  ProductionOrderCompleteData,
  ProductionOrderSearchModel,
  ProductivityRow,
  ProductivityReportFilters,
  MaterialBalanceRow,
  MaterialRequirementsResult,
  OrderCostRow,
  ReportPeriodFilter,
} from '../types/production';
import { PaginatedResponse, ApiResponse } from '../types/common';

class ProductionService {
  /**
   * Normalizes paginated API responses from the backend.
   * Handles the shapes the Go backend may return:
   *   - { success, data: { data: [...], pagination: { page, pageSize, totalItems, totalPages } } }
   *   - { data: [...], pagination: {...} }
   *   - [...] direct array
   */
  private static normalizePaginatedResponse<T>(json: any): PaginatedResponse<T> {
    let items: T[] = [];
    let page = 1;
    let limit = 20;
    let total = 0;
    let totalPages = 1;

    if (Array.isArray(json)) {
      items = json;
      total = json.length;
    } else if (json && typeof json === 'object') {
      if (Array.isArray(json.data)) {
        items = json.data;
      } else if (json.data && Array.isArray(json.data.data)) {
        items = json.data.data;
      } else if (json.data && Array.isArray(json.data.items)) {
        items = json.data.items;
      } else if (Array.isArray(json.items)) {
        items = json.items;
      }

      // Extract pagination — handles nested or flat, camelCase or snake_case
      const pag = json.pagination || json.data?.pagination || json;
      page = pag.page ?? page;
      limit = pag.pageSize ?? pag.page_size ?? pag.limit ?? limit;
      total = pag.totalItems ?? pag.total_items ?? pag.total ?? json.total ?? items.length;
      totalPages =
        pag.totalPages ?? pag.total_pages ?? json.total_pages ?? Math.max(1, Math.ceil(total / limit));
    }

    return {
      success: true,
      data: items,
      pagination: { page, limit, total, totalPages },
    };
  }

  /**
   * Get authenticated API client
   */
  private async getApiClient() {
    return authService.getApiClient();
  }

  private async getBaseUrl(): Promise<string> {
    const { getConfig } = await import('../config');
    return getConfig().api.baseUrl;
  }

  /**
   * Extrai a mensagem de erro do backend, com fallback
   */
  private static async extractErrorMessage(response: any, fallback: string): Promise<string> {
    try {
      const data = await response.json();
      return data?.message || data?.error || fallback;
    } catch {
      return fallback;
    }
  }

  // ============================================================
  // Atividades — /production/activities
  // ============================================================

  async listActivities(
    searchModel: ProductionActivitySearchModel
  ): Promise<PaginatedResponse<ProductionActivity>> {
    const params = new URLSearchParams({
      page: searchModel.page.toString(),
      limit: searchModel.pageSize.toString(),
      ...(searchModel.searchName && { q: searchModel.searchName }),
      ...(searchModel.category !== undefined && { category: searchModel.category.toString() }),
    });

    if (searchModel.is_active !== undefined) {
      params.append('is_active', searchModel.is_active.toString());
    }

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/production/activities?${params}`);

    if (!response.ok) {
      throw new Error('Falha ao buscar atividades de produção');
    }

    const jsonResponse = await response.json();
    return ProductionService.normalizePaginatedResponse<ProductionActivity>(jsonResponse);
  }

  async getActivity(id: number): Promise<ProductionActivity> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/production/activities/${id}`);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Atividade não encontrada');
      }
      throw new Error('Falha ao buscar atividade');
    }

    const data: ApiResponse<ProductionActivity> = await response.json();
    return data.data!;
  }

  async createActivity(data: ProductionActivityFormData): Promise<ProductionActivity> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/production/activities', data);

    if (!response.ok) {
      if (response.status === 409) {
        throw new Error(
          await ProductionService.extractErrorMessage(
            response,
            `Código '${data.code}' já está em uso. Por favor, utilize um código único.`
          )
        );
      }
      if (response.status === 400) {
        throw new Error(await ProductionService.extractErrorMessage(response, 'Dados inválidos'));
      }
      throw new Error('Falha ao criar atividade');
    }

    const result: ApiResponse<ProductionActivity> = await response.json();
    return result.data!;
  }

  async updateActivity(
    id: number,
    data: Partial<Omit<ProductionActivityFormData, 'code'>>
  ): Promise<ProductionActivity> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/production/activities/${id}`, data);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Atividade não encontrada');
      }
      if (response.status === 400) {
        throw new Error(await ProductionService.extractErrorMessage(response, 'Dados inválidos'));
      }
      throw new Error('Falha ao atualizar atividade');
    }

    const result: ApiResponse<ProductionActivity> = await response.json();
    return result.data!;
  }

  async deleteActivity(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/production/activities/${id}`);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Atividade não encontrada');
      }
      if (response.status === 409) {
        throw new Error(
          await ProductionService.extractErrorMessage(
            response,
            'Atividade está vinculada a outros registros e não pode ser excluída'
          )
        );
      }
      throw new Error('Falha ao excluir atividade');
    }
  }

  // ============================================================
  // Colaboradores — /production/employees
  // ============================================================

  async listEmployees(
    searchModel: ProductionEmployeeSearchModel
  ): Promise<PaginatedResponse<ProductionEmployee>> {
    const params = new URLSearchParams({
      page: searchModel.page.toString(),
      limit: searchModel.pageSize.toString(),
      ...(searchModel.searchName && { q: searchModel.searchName }),
    });

    if (searchModel.is_active !== undefined) {
      params.append('is_active', searchModel.is_active.toString());
    }

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/production/employees?${params}`);

    if (!response.ok) {
      throw new Error('Falha ao buscar colaboradores');
    }

    const jsonResponse = await response.json();
    return ProductionService.normalizePaginatedResponse<ProductionEmployee>(jsonResponse);
  }

  async getEmployee(id: number): Promise<ProductionEmployee> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/production/employees/${id}`);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Colaborador não encontrado');
      }
      throw new Error('Falha ao buscar colaborador');
    }

    const data: ApiResponse<ProductionEmployee> = await response.json();
    return data.data!;
  }

  async createEmployee(data: ProductionEmployeeFormData): Promise<ProductionEmployee> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/production/employees', data);

    if (!response.ok) {
      if (response.status === 400) {
        throw new Error(await ProductionService.extractErrorMessage(response, 'Dados inválidos'));
      }
      throw new Error('Falha ao criar colaborador');
    }

    const result: ApiResponse<ProductionEmployee> = await response.json();
    return result.data!;
  }

  async updateEmployee(
    id: number,
    data: Partial<ProductionEmployeeFormData>
  ): Promise<ProductionEmployee> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/production/employees/${id}`, data);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Colaborador não encontrado');
      }
      if (response.status === 400) {
        throw new Error(await ProductionService.extractErrorMessage(response, 'Dados inválidos'));
      }
      throw new Error('Falha ao atualizar colaborador');
    }

    const result: ApiResponse<ProductionEmployee> = await response.json();
    return result.data!;
  }

  async deleteEmployee(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/production/employees/${id}`);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Colaborador não encontrado');
      }
      if (response.status === 409) {
        throw new Error(
          await ProductionService.extractErrorMessage(
            response,
            'Colaborador está vinculado a outros registros e não pode ser excluído'
          )
        );
      }
      throw new Error('Falha ao excluir colaborador');
    }
  }

  // ============================================================
  // Lista de Materiais (BOM) — /production/bill-of-materials
  // ============================================================

  async listBillOfMaterials(
    searchModel: BillOfMaterialSearchModel
  ): Promise<PaginatedResponse<BillOfMaterial>> {
    const params = new URLSearchParams({
      page: searchModel.page.toString(),
      limit: searchModel.pageSize.toString(),
      ...(searchModel.searchName && { q: searchModel.searchName }),
      ...(searchModel.product_id !== undefined && { product_id: searchModel.product_id.toString() }),
      ...(searchModel.raw_material_id !== undefined && {
        raw_material_id: searchModel.raw_material_id.toString(),
      }),
    });

    if (searchModel.is_active !== undefined) {
      params.append('is_active', searchModel.is_active.toString());
    }

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/production/bill-of-materials?${params}`);

    if (!response.ok) {
      throw new Error('Falha ao buscar listas de materiais');
    }

    const jsonResponse = await response.json();
    return ProductionService.normalizePaginatedResponse<BillOfMaterial>(jsonResponse);
  }

  async getBillOfMaterial(id: number): Promise<BillOfMaterial> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/production/bill-of-materials/${id}`);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Lista de materiais não encontrada');
      }
      throw new Error('Falha ao buscar lista de materiais');
    }

    const data: ApiResponse<BillOfMaterial> = await response.json();
    return data.data!;
  }

  async createBillOfMaterial(data: BillOfMaterialCreateData): Promise<BillOfMaterial> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/production/bill-of-materials', data);

    if (!response.ok) {
      if (response.status === 409) {
        throw new Error(
          await ProductionService.extractErrorMessage(
            response,
            'Já existe uma lista de materiais para este item'
          )
        );
      }
      if (response.status === 400) {
        throw new Error(await ProductionService.extractErrorMessage(response, 'Dados inválidos'));
      }
      throw new Error('Falha ao criar lista de materiais');
    }

    const result: ApiResponse<BillOfMaterial> = await response.json();
    return result.data!;
  }

  async updateBillOfMaterial(id: number, data: BillOfMaterialUpdateData): Promise<BillOfMaterial> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/production/bill-of-materials/${id}`, data);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Lista de materiais não encontrada');
      }
      if (response.status === 400) {
        throw new Error(await ProductionService.extractErrorMessage(response, 'Dados inválidos'));
      }
      throw new Error('Falha ao atualizar lista de materiais');
    }

    const result: ApiResponse<BillOfMaterial> = await response.json();
    return result.data!;
  }

  async deleteBillOfMaterial(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/production/bill-of-materials/${id}`);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Lista de materiais não encontrada');
      }
      if (response.status === 409) {
        throw new Error(
          await ProductionService.extractErrorMessage(
            response,
            'Lista de materiais está vinculada a outros registros e não pode ser excluída'
          )
        );
      }
      throw new Error('Falha ao excluir lista de materiais');
    }
  }

  // ============================================================
  // Apontamentos — /production/activity-logs
  // ============================================================

  async listActivityLogs(
    searchModel: ProductionActivityLogSearchModel
  ): Promise<PaginatedResponse<ProductionActivityLog>> {
    const params = new URLSearchParams({
      page: searchModel.page.toString(),
      limit: searchModel.pageSize.toString(),
      ...(searchModel.date_from && { date_from: searchModel.date_from }),
      ...(searchModel.date_to && { date_to: searchModel.date_to }),
      ...(searchModel.employee_id !== undefined && {
        employee_id: searchModel.employee_id.toString(),
      }),
      ...(searchModel.activity_id !== undefined && {
        activity_id: searchModel.activity_id.toString(),
      }),
      ...(searchModel.production_order_id !== undefined && {
        production_order_id: searchModel.production_order_id.toString(),
      }),
    });

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/production/activity-logs?${params}`);

    if (!response.ok) {
      throw new Error('Falha ao buscar apontamentos de produção');
    }

    const jsonResponse = await response.json();
    return ProductionService.normalizePaginatedResponse<ProductionActivityLog>(jsonResponse);
  }

  async getActivityLog(id: number): Promise<ProductionActivityLog> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/production/activity-logs/${id}`);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Apontamento não encontrado');
      }
      throw new Error('Falha ao buscar apontamento');
    }

    const data: ApiResponse<ProductionActivityLog> = await response.json();
    return data.data!;
  }

  async createActivityLog(data: ProductionActivityLogCreateData): Promise<ProductionActivityLog> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/production/activity-logs', data);

    if (!response.ok) {
      if (response.status === 400) {
        throw new Error(await ProductionService.extractErrorMessage(response, 'Dados inválidos'));
      }
      throw new Error('Falha ao registrar apontamento');
    }

    const result: ApiResponse<ProductionActivityLog> = await response.json();
    return result.data!;
  }

  async updateActivityLog(
    id: number,
    data: ProductionActivityLogUpdateData
  ): Promise<ProductionActivityLog> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/production/activity-logs/${id}`, data);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Apontamento não encontrado');
      }
      if (response.status === 400) {
        throw new Error(await ProductionService.extractErrorMessage(response, 'Dados inválidos'));
      }
      throw new Error('Falha ao atualizar apontamento');
    }

    const result: ApiResponse<ProductionActivityLog> = await response.json();
    return result.data!;
  }

  async deleteActivityLog(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/production/activity-logs/${id}`);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Apontamento não encontrado');
      }
      throw new Error('Falha ao excluir apontamento');
    }
  }

  // ============================================================
  // Ordens de Produção — /production/orders
  // ============================================================

  async listProductionOrders(
    searchModel: ProductionOrderSearchModel
  ): Promise<PaginatedResponse<ProductionOrder>> {
    const params = new URLSearchParams({
      page: searchModel.page.toString(),
      limit: searchModel.pageSize.toString(),
      ...(searchModel.searchName && { q: searchModel.searchName }),
      ...(searchModel.status !== undefined && { status: searchModel.status.toString() }),
      ...(searchModel.date_from && { date_from: searchModel.date_from }),
      ...(searchModel.date_to && { date_to: searchModel.date_to }),
    });

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/production/orders?${params}`);

    if (!response.ok) {
      throw new Error('Falha ao buscar ordens de produção');
    }

    const jsonResponse = await response.json();
    return ProductionService.normalizePaginatedResponse<ProductionOrder>(jsonResponse);
  }

  async getProductionOrder(id: number): Promise<ProductionOrder> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/production/orders/${id}`);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Ordem de produção não encontrada');
      }
      throw new Error('Falha ao buscar ordem de produção');
    }

    const data: ApiResponse<ProductionOrder> = await response.json();
    return data.data!;
  }

  async createProductionOrder(data: ProductionOrderCreateData): Promise<ProductionOrder> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/production/orders', data);

    if (!response.ok) {
      if (response.status === 409) {
        throw new Error(
          await ProductionService.extractErrorMessage(
            response,
            `Código '${data.code}' já está em uso. Por favor, utilize um código único.`
          )
        );
      }
      if (response.status === 400) {
        throw new Error(await ProductionService.extractErrorMessage(response, 'Dados inválidos'));
      }
      throw new Error('Falha ao criar ordem de produção');
    }

    const result: ApiResponse<ProductionOrder> = await response.json();
    return result.data!;
  }

  async updateProductionOrder(id: number, data: ProductionOrderUpdateData): Promise<ProductionOrder> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/production/orders/${id}`, data);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Ordem de produção não encontrada');
      }
      if (response.status === 400) {
        throw new Error(await ProductionService.extractErrorMessage(response, 'Dados inválidos'));
      }
      throw new Error('Falha ao atualizar ordem de produção');
    }

    const result: ApiResponse<ProductionOrder> = await response.json();
    return result.data!;
  }

  private async postProductionOrderAction(
    id: number,
    action: 'start' | 'cancel',
    fallbackError: string
  ): Promise<ProductionOrder> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/production/orders/${id}/${action}`, {});

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Ordem de produção não encontrada');
      }
      if (response.status === 400) {
        throw new Error(await ProductionService.extractErrorMessage(response, fallbackError));
      }
      throw new Error(fallbackError);
    }

    const result: ApiResponse<ProductionOrder> = await response.json();
    return result.data!;
  }

  async startProductionOrder(id: number): Promise<ProductionOrder> {
    return this.postProductionOrderAction(id, 'start', 'Falha ao iniciar a produção');
  }

  async cancelProductionOrder(id: number): Promise<ProductionOrder> {
    return this.postProductionOrderAction(id, 'cancel', 'Falha ao cancelar a ordem de produção');
  }

  async completeProductionOrder(
    id: number,
    data: ProductionOrderCompleteData
  ): Promise<ProductionOrder> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/production/orders/${id}/complete`, data);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Ordem de produção não encontrada');
      }
      if (response.status === 400) {
        throw new Error(
          await ProductionService.extractErrorMessage(response, 'Falha ao concluir a produção')
        );
      }
      throw new Error('Falha ao concluir a produção');
    }

    const result: ApiResponse<ProductionOrder> = await response.json();
    return result.data!;
  }

  // ============================================================
  // Relatório: Produtos a Fabricar
  // ============================================================

  async getPendingManufacturing(): Promise<PendingManufacturingProduct[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get('/production/pending-manufacturing');

    if (!response.ok) {
      throw new Error('Falha ao buscar produtos a fabricar');
    }

    const jsonResponse = await response.json();
    const payload = jsonResponse?.data;

    if (Array.isArray(payload)) {
      return payload;
    }
    if (payload && Array.isArray(payload.data)) {
      return payload.data;
    }
    return [];
  }

  // ============================================================
  // Relatórios de Produção — /production/reports/*
  // ============================================================

  /**
   * Busca um relatório cujo payload é { success, data: { data: rows[] } }
   * e retorna apenas o array de linhas.
   */
  private async getReportRows<T>(
    path: string,
    params: URLSearchParams,
    fallbackError: string
  ): Promise<T[]> {
    const apiClient = await this.getApiClient();
    const query = params.toString();
    const response = await apiClient.get(query ? `${path}?${query}` : path);

    if (!response.ok) {
      throw new Error(await ProductionService.extractErrorMessage(response, fallbackError));
    }

    const jsonResponse = await response.json();
    const payload = jsonResponse?.data;

    if (Array.isArray(payload)) {
      return payload;
    }
    if (payload && Array.isArray(payload.data)) {
      return payload.data;
    }
    return [];
  }

  private static buildPeriodParams(filters: ReportPeriodFilter): URLSearchParams {
    const params = new URLSearchParams();
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    return params;
  }

  /** Relatório de produtividade por colaborador × atividade */
  async getProductivityReport(filters: ProductivityReportFilters): Promise<ProductivityRow[]> {
    const params = ProductionService.buildPeriodParams(filters);
    if (filters.employee_id !== undefined) {
      params.append('employee_id', filters.employee_id.toString());
    }
    if (filters.activity_id !== undefined) {
      params.append('activity_id', filters.activity_id.toString());
    }
    return this.getReportRows<ProductivityRow>(
      '/production/reports/productivity',
      params,
      'Falha ao buscar relatório de produtividade'
    );
  }

  /** Balanço de materiais (fluxos físicos × estoque registrado) */
  async getMaterialBalanceReport(filters: ReportPeriodFilter): Promise<MaterialBalanceRow[]> {
    return this.getReportRows<MaterialBalanceRow>(
      '/production/reports/material-balance',
      ProductionService.buildPeriodParams(filters),
      'Falha ao buscar balanço de materiais'
    );
  }

  /** Necessidade de compra/produção (demanda = pedidos em digitação) */
  async getMaterialRequirements(): Promise<MaterialRequirementsResult> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get('/production/reports/material-requirements');

    if (!response.ok) {
      throw new Error(
        await ProductionService.extractErrorMessage(
          response,
          'Falha ao buscar necessidade de materiais'
        )
      );
    }

    const jsonResponse = await response.json();
    const payload = jsonResponse?.data ?? {};

    return {
      products: Array.isArray(payload.products) ? payload.products : [],
      materials: Array.isArray(payload.materials) ? payload.materials : [],
      missing_bom_products: Array.isArray(payload.missing_bom_products)
        ? payload.missing_bom_products
        : undefined,
    };
  }

  /** Custo por Ordem de Produção concluída no período */
  async getOrderCostReport(filters: ReportPeriodFilter): Promise<OrderCostRow[]> {
    return this.getReportRows<OrderCostRow>(
      '/production/reports/order-cost',
      ProductionService.buildPeriodParams(filters),
      'Falha ao buscar custo por ordem de produção'
    );
  }

  async downloadPendingManufacturingPdf(): Promise<void> {
    const baseUrl = await this.getBaseUrl();

    const doFetch = async (token?: string | null) =>
      fetch(`${baseUrl}/production/pending-manufacturing/pdf`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

    let response = await doFetch();
    if (response.status === 401) {
      const token = await authService.getAuthToken(false);
      response = await doFetch(token);
    }
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.message || `Erro ao gerar PDF (${response.status})`);
    }

    const disposition = response.headers.get('content-disposition') ?? '';
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch?.[1] ?? 'produtos-a-fabricar.pdf';

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }
}

// Singleton instance
export const productionService = new ProductionService();
export default productionService;
